import React, { ReactNode, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { Col, Row } from 'antd'
import { Formik, FormikProps } from 'formik'
import * as Yup from 'yup'

import question2Api, { Step2Question2Values } from 'core/application/ichraDecisionTool/prospectSignup/step2/question2'

import Tooltip from 'common/components/dataDisplay/Tooltip'
import Checkbox from 'common/components/dataEntry/components/Checkbox'
import FormikCheckboxGroupField from 'common/components/dataEntry/formik/FormikCheckboxGroupField'
import Input from 'common/components/dataEntry/formik/FormikDefaultInputField'
import notification from 'common/components/feedback/Notification'
import Button from 'common/components/general/Button'

import { commandRequest } from 'common/RequestUtils'
import { BaseNavigationProps } from 'common/wizard/BaseNavigationProps'

import CategoryItemPageHeader from '../common/CategoryItemPageHeader'

const OTHER_ID = 5

const formValidator = (translate: any) => {
  return Yup.object({
    whyConsiderAlternatives: Yup.array().min(1, translate('at-least-one')).required(translate('required')),
    whyConsiderAlternativesOther: Yup.string()
      .nullable()
      .when('whyConsiderAlternatives', {
        is: (whyConsiderAlternatives: number[]) => whyConsiderAlternatives.includes(OTHER_ID),
        then: Yup.string().trim().nullable().typeError('Required').required('Required'),
      }),
  })
}

export interface Step2Question2Props extends Step2Question2Values {}

const Question2: React.FC<BaseNavigationProps<Step2Question2Props>> = (props) => {
  const { t: translate } = useTranslation('ichra-decision-tool')
  const [loading, setLoading] = useState<boolean>(false)

  const onSubmit = async (values: Step2Question2Props) => {
    setLoading(true)
    const allValues: Step2Question2Values = {
      ...values,
      whyConsiderAlternativesOther: values.whyConsiderAlternatives.includes(OTHER_ID)
        ? values.whyConsiderAlternativesOther
        : '',
    }
    const res = await commandRequest(() => question2Api(allValues))
    setLoading(false)
    if (!res.errors) {
      props.goNext(props.name)
    } else {
      notification.open({
        message: res.errors[0],
        type: 'error',
      })
    }
  }

  const getNextButton = (formik: FormikProps<Step2Question2Props>): ReactNode => {
    const nextButton = (
      <Button
        icon="fa fa-arrow-right"
        iconPosition="right"
        size="large"
        htmlType="submit"
        type="primary"
        disabled={!formValidator(translate).isValidSync(formik.values)}
        className="float-right next__button"
        loading={loading}
      >
        {translate('next')}
      </Button>
    )

    const missingInput = !formValidator(translate).isValidSync(formik.values)

    return missingInput ? (
      <Tooltip title={translate('next-tooltip')} position="left">
        {nextButton}
      </Tooltip>
    ) : (
      nextButton
    )
  }

  return (
    <Formik
      enableReinitialize={true}
      initialValues={props.state}
      validationSchema={formValidator(translate)}
      onSubmit={onSubmit}
      validate={(values) => {
        props.hasDirtyData(true)
      }}
    >
      {(formik) => (
        <form onSubmit={formik.handleSubmit} autoComplete="off" onBlur={formik.handleBlur}>
          <Row>
            <Col xs={24} sm={24} md={13}>
              <CategoryItemPageHeader
                title={translate('prospect-signup.step-2.question-1-title')}
                numberOfQuestions={6}
                currentQuestion={2}
                question={translate('prospect-signup.step-2.question-2')}
                className="ml-0"
                titleFontSize={20}
              />
            </Col>
          </Row>

          <Row className="form__row">
            <Col xs={24} sm={24} md={12}>
              <FormikCheckboxGroupField
                defaultValue={props.state.whyConsiderAlternatives}
                name="whyConsiderAlternatives"
              >
                {props.state.whyConsiderAlternativesOptions.map((alternative) => (
                  <>
                    <Checkbox value={alternative.id} key={alternative.id} white className="mb-8 ml-0">
                      {alternative.name}
                    </Checkbox>
                  </>
                ))}
                {formik.values.whyConsiderAlternatives.includes(OTHER_ID) && (
                  <Input name="whyConsiderAlternativesOther" placeholder={translate('please-specify')} />
                )}
              </FormikCheckboxGroupField>
            </Col>
          </Row>

          <Row>
            <Col xs={24}>
              <div className="bottom__row">
                <Button
                  icon="fa fa-arrow-left"
                  size="large"
                  htmlType="button"
                  type="default"
                  onClick={() => {
                    props.goBack(props.name)
                  }}
                >
                  {translate('back')}
                </Button>
                {getNextButton(formik)}
              </div>
            </Col>
          </Row>
        </form>
      )}
    </Formik>
  )
}

export default Question2
